import { Component, Input } from '@angular/core';
import {
    AttachmentHandler,
    FormResponseCommand,
    FormResponseFormGroup,
    MissionDetailsDto,
    MissionService,
    NameValue,
    PersonsOrganisationDto,
    SortieDetailsDto,
    SortieDto,
    StepEntityRequiredDocumentation,
    WorkTracker
} from '@flyfreely-portal-ui/flyfreely';
import { WorkspaceStateService } from '@flyfreely-portal-ui/workspace';
import { DocumentationActions } from 'libs/documentation/src/lib/interfaces';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'flight-documentation-dialogue',
    template: `
        <div class="modal-header no-icon">
            <h4 id="dialog-child-name" class="modal-title pull-left">
                Flight Documentation
            </h4>
            <button
                type="button"
                class="close pull-right"
                aria-label="Close"
                (click)="close()"
            >
                <span aria-hidden="true">&times;</span>
            </button>
        </div>
        <div class="modal-body">
            <div>
                <div class="data">
                    <span class="display-label">Status</span>
                    {{ flight.status }}
                </div>
                <div class="data">
                    <span class="display-label">Time of Day</span>
                    {{ flight.timeOfDay }}
                </div>
                <div class="data">
                    <span class="display-label">Flight Time Source</span>
                    {{ flight.durationSource }}
                </div>
                <div class="data">
                    <span class="display-label">Start Time</span>
                    {{ flight.recordedStartTime | formatDateTime }}
                </div>
                <div class="data">
                    <span class="display-label">End Time</span>
                    {{ flight.recordedEndTime | formatDateTime }}
                </div>
                <h5>Reason</h5>
                <div class="min-height-field pre-line">
                    {{ flight.reason }}
                </div>

                <h5>Notes</h5>
                <div class="min-height-field pre-line">
                    {{ flight.notes }}
                </div>
                <h4>Documentation</h4>
                <documentation-list
                    title="Flight #{{ flight.number }}"
                    [organisationId]="mission.organisationId"
                    [requiredDocumentation]="documentation"
                    [formResponses]="formResponses"
                    [steps]="steps"
                    [attachmentsHandler]="attachmentsHandler"
                    [documentationActions]="documentationActions"
                    [missionRequiredDocumentation]="documentation"
                ></documentation-list>

                <h4>Attachments</h4>
                <attachment-list
                    [attachmentsHandler]="attachmentsHandler"
                    [allowEdit]="false"
                    [allowDelete]="false"
                    [allowUploads]="false"
                    [parentPromiseTracker]="workTracker"
                    permissions
                ></attachment-list>

                <h4>Crew</h4>
                <table *ngIf="flightDetails != null" class="table">
                    <thead>
                        <tr>
                            <th>Role</th>
                            <th>Person</th>
                            <th>Notes</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr *ngFor="let crewMember of flightDetails.crew">
                            <td>{{ crewMember?.missionRole?.name }}</td>
                            <td>{{ crewMember?.person | formatPerson }}</td>
                            <td>{{ crewMember?.notes }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    `,
    styles: [
        `
            .min-height-field {
                min-height: 2rem;
            }
        `
    ]
})
export class FlightDocumentationDialogue {
    @Input() mission: MissionDetailsDto;
    @Input() flight: SortieDto;
    @Input() documentation: StepEntityRequiredDocumentation;
    @Input() formResponses: FormResponseFormGroup;

    organisation: PersonsOrganisationDto;

    attachmentsHandler: AttachmentHandler;
    documentationActions: DocumentationActions;

    flightDetails: SortieDetailsDto;

    working = false;
    loaded = false;
    workTracker = new WorkTracker();

    private ngUnsubscribe$ = new Subject<void>();

    steps: NameValue[];

    constructor(
        private modal: BsModalRef,
        private missionService: MissionService,
        private workspaceStateService: WorkspaceStateService
    ) {
        this.steps = missionService.getFlightSteps();
    }

    ngOnInit() {
        this.workspaceStateService.currentLoadedOrganisation$
            .pipe(take(1), takeUntil(this.ngUnsubscribe$))
            .subscribe(organisation => {
                this.organisation = organisation.organisation;
                this.setup();
            });
    }

    private setup() {
        this.attachmentsHandler = this.missionService.flightAttachmentHandler(
            this.mission.id,
            this.flight.id,
            this.organisation.id
        );
        this.attachmentsHandler.destroyOn(this.ngUnsubscribe$);

        this.setupDocumentationActions();

        this.missionService
            .findFlight(this.mission.id, this.flight.id, this.organisation.id)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(flightDetails => (this.flightDetails = flightDetails));
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    close() {
        this.modal.hide();
    }

    private setupDocumentationActions() {
        this.documentationActions = {
            getFormTemplatePrintUrl: (checklistId: number, step: string) =>
                this.missionService.getFlightFormTemplatePrintUrl(
                    this.mission.id,
                    this.flight.id,
                    checklistId,
                    step,
                    this.organisation.id
                ),
            getFormResponsePrintUrl: (
                formId: number,
                step: string,
                relatedEntityType?: string,
                relatedEntityId?: number
            ) =>
                this.missionService.getFlightFormResponsePrintUrl(
                    this.mission.id,
                    this.flight.id,
                    formId,
                    step,
                    this.organisation.id,
                    relatedEntityType,
                    relatedEntityId
                ),
            storeFormResponse: (
                formId: number,
                step: string,
                response: FormResponseCommand
            ) => Promise.reject()
        };
    }
}
